import React, { useState } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { useMediaQuery } from 'react-responsive';
import {
  Box, Container, Typography, Paper, Grid, CircularProgress,
  Button, TextField, Alert, Chip, LinearProgress, Select, MenuItem,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  TableSortLabel,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import DownloadIcon from '@mui/icons-material/Download';

import { useUserAuth } from '../../context/UserAuthContext';
import Header from '../../components/Header/Header';
import Logo from '../../components/Logo/Logo';

// ─── helpers ─────────────────────────────────────────────────────────────────

function fmtCurrency(val) {
  if (val === null || val === undefined) return '—';
  return `$${Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(val) {
  if (!val) return 'No expiry';
  try { return format(typeof val === 'string' ? parseISO(val) : val, 'MMM d, yyyy'); }
  catch { return val; }
}

function parseInputIds(raw) {
  return raw.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);
}

const STATUS_CHIP = {
  incomplete: { label: 'Incomplete', color: '#e65100', bg: '#fff3e0' },
  completed:  { label: 'Completed',  color: '#2e7d32', bg: '#e8f5e9' },
};

function StatusChip({ status }) {
  const cfg = STATUS_CHIP[status] || { label: status, color: '#555', bg: '#eee' };
  return (
    <Chip label={cfg.label} size="small" sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: '0.7rem' }} />
  );
}

// ─── summary cards ────────────────────────────────────────────────────────────

function KpiCard({ label, value, color }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="h4" fontWeight={700} sx={{ color: color || 'text.primary', lineHeight: 1.2, mt: 0.5 }}>
        {value ?? '—'}
      </Typography>
    </Paper>
  );
}

// ─── results table ────────────────────────────────────────────────────────────

const FILTER_INPUT_SX = {
  '& .MuiInputBase-root': { fontSize: '0.72rem', bgcolor: 'white' },
  '& .MuiInputBase-input': { py: '3px', px: '6px' },
};

function ResultsTable({ results, onExportCSV }) {
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [filters, setFilters] = useState({ memberId: '', offerName: '', status: '', liability: '' });

  const setFilter = (key, val) => setFilters(f => ({ ...f, [key]: val }));

  function handleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  }

  // Flatten all rows
  const allRows = results.flatMap(member =>
    member.offerStates.length > 0
      ? member.offerStates.map(s => ({ ...s, memberId: member.memberId, sparkflyId: member.sparkflyId }))
      : [{ memberId: member.memberId, sparkflyId: member.sparkflyId, _empty: true, error: member.error }]
  );

  const statuses = [...new Set(allRows.filter(r => r.status).map(r => r.status))].sort();

  // Filter
  let rows = allRows.filter(row => {
    if (row._empty) return true;
    if (filters.memberId && !row.memberId.toLowerCase().includes(filters.memberId.toLowerCase())) return false;
    if (filters.offerName && !(row.offerName || '').toLowerCase().includes(filters.offerName.toLowerCase())) return false;
    if (filters.status && row.status !== filters.status) return false;
    if (filters.liability === 'yes' && !row.isLiability) return false;
    if (filters.liability === 'no' && (row.isLiability || row._empty)) return false;
    return true;
  });

  // Sort
  if (sortCol) {
    rows = [...rows].sort((a, b) => {
      const aVal = a[sortCol] ?? '';
      const bVal = b[sortCol] ?? '';
      if (sortCol === 'remainingBalance') {
        return sortDir === 'asc' ? (Number(aVal) || 0) - (Number(bVal) || 0) : (Number(bVal) || 0) - (Number(aVal) || 0);
      }
      if (sortCol === 'expiresAt') {
        const aT = aVal ? new Date(aVal).getTime() : Infinity;
        const bT = bVal ? new Date(bVal).getTime() : Infinity;
        return sortDir === 'asc' ? aT - bT : bT - aT;
      }
      return sortDir === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }

  // Alternating member group colors on the filtered+sorted result
  const memberColors = {};
  let colorIdx = 0;
  rows.forEach(row => {
    if (!(row.memberId in memberColors)) memberColors[row.memberId] = colorIdx++ % 2 === 0 ? 'white' : '#fafafa';
  });

  const hasFilters = Object.values(filters).some(Boolean);

  function SortableHeader({ col, label, align }) {
    return (
      <TableCell align={align} sx={{ fontWeight: 700, whiteSpace: 'nowrap', verticalAlign: 'bottom', pb: 0.5 }}>
        <TableSortLabel
          active={sortCol === col}
          direction={sortCol === col ? sortDir : 'asc'}
          onClick={() => handleSort(col)}
        >
          {label}
        </TableSortLabel>
      </TableCell>
    );
  }

  return (
    <Paper variant="outlined">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, pt: 1.5, pb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {rows.length.toLocaleString()} row{rows.length !== 1 ? 's' : ''}
          {hasFilters && allRows.length !== rows.length && ` (filtered from ${allRows.length.toLocaleString()})`}
        </Typography>
        <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={onExportCSV}>
          Export CSV
        </Button>
      </Box>
      <TableContainer sx={{ maxHeight: 600 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            {/* Sort row */}
            <TableRow>
              <SortableHeader col="memberId"          label="Member ID" />
              <SortableHeader col="sparkflyId"        label="Sparkfly ID" />
              <SortableHeader col="offerName"         label="Offer Name" />
              <SortableHeader col="status"            label="Status" />
              <SortableHeader col="remainingBalance"  label="Remaining Balance" align="right" />
              <SortableHeader col="expiresAt"         label="Expires" />
              <SortableHeader col="isLiability"       label="Liability" align="center" />
            </TableRow>
            {/* Filter row */}
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell sx={{ py: 0.5 }}>
                <TextField
                  size="small" placeholder="Filter…" value={filters.memberId}
                  onChange={e => setFilter('memberId', e.target.value)}
                  sx={FILTER_INPUT_SX} fullWidth
                />
              </TableCell>
              <TableCell sx={{ py: 0.5 }} />
              <TableCell sx={{ py: 0.5 }}>
                <TextField
                  size="small" placeholder="Filter…" value={filters.offerName}
                  onChange={e => setFilter('offerName', e.target.value)}
                  sx={FILTER_INPUT_SX} fullWidth
                />
              </TableCell>
              <TableCell sx={{ py: 0.5 }}>
                <Select
                  size="small" value={filters.status} displayEmpty
                  onChange={e => setFilter('status', e.target.value)}
                  sx={{ fontSize: '0.72rem', minWidth: 110, bgcolor: 'white', '& .MuiSelect-select': { py: '3px', px: '6px' } }}
                >
                  <MenuItem value=""><em>All</em></MenuItem>
                  {statuses.map(s => (
                    <MenuItem key={s} value={s} sx={{ fontSize: '0.72rem' }}>
                      {STATUS_CHIP[s]?.label || s}
                    </MenuItem>
                  ))}
                </Select>
              </TableCell>
              <TableCell sx={{ py: 0.5 }} />
              <TableCell sx={{ py: 0.5 }} />
              <TableCell sx={{ py: 0.5 }} align="center">
                <Select
                  size="small" value={filters.liability} displayEmpty
                  onChange={e => setFilter('liability', e.target.value)}
                  sx={{ fontSize: '0.72rem', minWidth: 80, bgcolor: 'white', '& .MuiSelect-select': { py: '3px', px: '6px' } }}
                >
                  <MenuItem value=""><em>All</em></MenuItem>
                  <MenuItem value="yes" sx={{ fontSize: '0.72rem' }}>Yes</MenuItem>
                  <MenuItem value="no"  sx={{ fontSize: '0.72rem' }}>No</MenuItem>
                </Select>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" py={3}>No rows match the current filters.</Typography>
                </TableCell>
              </TableRow>
            ) : rows.map((row, i) => {
              const bg = memberColors[row.memberId];
              if (row._empty) {
                return (
                  <TableRow key={i} sx={{ bgcolor: bg }}>
                    <TableCell><Typography variant="body2" fontFamily="monospace">{row.memberId}</Typography></TableCell>
                    <TableCell><Typography variant="body2" color="text.secondary">—</Typography></TableCell>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color={row.error === 'not_found' ? 'text.secondary' : 'error.main'} fontStyle="italic">
                        {row.error === 'not_found' ? 'Member not found' : `Error: ${row.error}`}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              }
              return (
                <TableRow key={i} hover sx={{ bgcolor: bg }}>
                  <TableCell><Typography variant="body2" fontFamily="monospace">{row.memberId}</Typography></TableCell>
                  <TableCell><Typography variant="body2" color="text.secondary">{row.sparkflyId}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{row.offerName}</Typography></TableCell>
                  <TableCell><StatusChip status={row.status} /></TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      fontWeight={row.remainingBalance > 0 ? 600 : 400}
                      sx={{ color: row.remainingBalance > 0 ? (row.isLiability ? '#e65100' : '#2e7d32') : 'text.secondary' }}
                    >
                      {fmtCurrency(row.remainingBalance)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="body2" color={row.expiresAt ? 'text.primary' : 'text.secondary'}>
                      {fmtDate(row.expiresAt)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {row.isLiability
                      ? <Chip label="Yes" size="small" sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 700, fontSize: '0.7rem' }} />
                      : <Typography variant="body2" color="text.secondary">—</Typography>
                    }
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

// ─── main component ───────────────────────────────────────────────────────────

export default function SparkflyMemberOffers() {
  const { user: firebaseUser } = useUserAuth();
  const isTabletOrMobile = useMediaQuery({ maxWidth: 1224 });

  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [summary, setSummary] = useState(null);
  const [progress, setProgress] = useState(null);

  const CHUNK_SIZE = 200;

  async function handleLookup() {
    const memberIds = parseInputIds(inputValue);
    if (memberIds.length === 0) return;

    const chunks = [];
    for (let i = 0; i < memberIds.length; i += CHUNK_SIZE) chunks.push(memberIds.slice(i, i + CHUNK_SIZE));

    setLoading(true);
    setError(null);
    setResults(null);
    setSummary(null);
    setProgress(chunks.length > 1 ? { chunk: 0, totalChunks: chunks.length, processed: 0, total: memberIds.length } : null);

    const allResults = [];
    const mergedByStatus = {};
    let membersFound = 0, membersNotFound = 0, membersErrored = 0;
    let totalOfferStates = 0, outstandingCount = 0, outstandingValue = 0;

    for (let i = 0; i < chunks.length; i++) {
      if (chunks.length > 1) setProgress({ chunk: i + 1, totalChunks: chunks.length, processed: i * CHUNK_SIZE, total: memberIds.length });

      try {
        const token = await firebaseUser.getIdToken(true);
        const { data } = await axios.post(
          '/hd-api/sparkfly/member-offers',
          { memberIds: chunks[i] },
          { headers: { Authorization: `Bearer ${token}` }, timeout: 300000 }
        );

        allResults.push(...data.results);
        membersFound    += data.summary.membersFound;
        membersNotFound += data.summary.membersNotFound;
        membersErrored  += data.summary.membersErrored;
        totalOfferStates += data.summary.totalOfferStates;
        outstandingCount += data.summary.outstandingCount;
        outstandingValue += data.summary.outstandingValue;

        for (const [status, vals] of Object.entries(data.summary.byStatus)) {
          if (!mergedByStatus[status]) mergedByStatus[status] = { count: 0, totalBalance: 0 };
          mergedByStatus[status].count += vals.count;
          mergedByStatus[status].totalBalance = Math.round((mergedByStatus[status].totalBalance + vals.totalBalance) * 100) / 100;
        }

        setResults([...allResults]);
        setSummary({
          membersRequested: memberIds.length,
          membersFound, membersNotFound, membersErrored,
          totalOfferStates, outstandingCount,
          outstandingValue: Math.round(outstandingValue * 100) / 100,
          byStatus: mergedByStatus,
        });
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Lookup failed');
        break;
      }
    }

    setProgress(null);
    setLoading(false);
  }

  function handleKeyDown(e) {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') handleLookup();
  }

  function handleExportCSV() {
    if (!results) return;
    const rows = results.flatMap(member =>
      member.offerStates.length > 0
        ? member.offerStates.map(s => ({
            member_id: member.memberId, sparkfly_id: member.sparkflyId,
            offer_state_id: s.offerStateId, offer_id: s.offerId, offer_name: s.offerName,
            status: s.status, remaining_balance: s.remainingBalance, expires_at: s.expiresAt || '',
            is_liability: s.isLiability, is_reward: s.isReward,
            reusable: s.reusable, transferable: s.transferable, campaign_id: s.campaignId || '',
          }))
        : [{ member_id: member.memberId, sparkfly_id: member.sparkflyId, offer_state_id: '', offer_id: '', offer_name: '', status: member.error || 'not_found', remaining_balance: '', expires_at: '', is_liability: '', is_reward: '', reusable: '', transferable: '', campaign_id: '' }]
    );

    const headers = ['member_id', 'sparkfly_id', 'offer_state_id', 'offer_id', 'offer_name', 'status', 'remaining_balance', 'expires_at', 'is_liability', 'is_reward', 'reusable', 'transferable', 'campaign_id'];
    const escape = val => `"${String(val ?? '').replace(/"/g, '""')}"`;
    const csv = [headers.join(','), ...rows.map(r => headers.map(h => escape(r[h])).join(','))].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `sparkfly-member-offers-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  const parsedCount = parseInputIds(inputValue).length;

  return (
    <div>
      <Header isTabletOrMobile={isTabletOrMobile} />
      <Container maxWidth="xl">
        <Logo isTabletOrMobile={isTabletOrMobile} />

        <Box sx={{ mt: 2 }}>
          {/* Page header */}
          <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider', mb: 2 }}>
            <Box sx={{
              backgroundColor: 'white',
              borderLeft: theme => `4px solid ${theme.palette.primary.main}`,
              px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2,
            }}>
              <Box sx={{
                backgroundColor: theme => alpha(theme.palette.primary.main, 0.1),
                borderRadius: 2, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <CardGiftcardIcon sx={{ fontSize: 32, color: 'primary.main' }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, fontFamily: 'Lato', color: 'text.primary' }}>
                  Sparkfly Member Offer Lookup
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Look up offer states and balances for loyalty members via the Sparkfly API
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Input */}
          <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 2 }}>
            <Box sx={{ borderLeft: theme => `4px solid ${theme.palette.primary.main}`, px: 3, py: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>Member IDs</Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <TextField
                  multiline rows={5} fullWidth
                  placeholder={'Enter member IDs, one per line (or comma-separated)\n\nExample:\n1234567890123456\n9876543210987654'}
                  value={inputValue}
                  onChange={e => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  size="small"
                  sx={{ '& textarea': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                  inputProps={{ spellCheck: false }}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 130 }}>
                  <Button
                    variant="contained"
                    onClick={handleLookup}
                    disabled={loading || parsedCount === 0}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {loading ? 'Looking up…' : 'Lookup'}
                  </Button>
                  {parsedCount > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                      {parsedCount} ID{parsedCount !== 1 ? 's' : ''}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', fontStyle: 'italic' }}>
                    Ctrl+Enter to run
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>

          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

          {progress && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  Processing chunk {progress.chunk} of {progress.totalChunks}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {Math.min(progress.processed + CHUNK_SIZE, progress.total).toLocaleString()} / {progress.total.toLocaleString()} members
                </Typography>
              </Box>
              <LinearProgress variant="determinate" value={(progress.chunk / progress.totalChunks) * 100} sx={{ height: 8, borderRadius: 4 }} />
              {results && results.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {results.length.toLocaleString()} members processed so far — results updating below
                </Typography>
              )}
            </Paper>
          )}

          {summary && (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={4} md={2}>
                  <KpiCard label="Members Requested" value={summary.membersRequested} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <KpiCard label="Members Found" value={summary.membersFound} color="#2e7d32" />
                </Grid>
                {summary.membersNotFound > 0 && (
                  <Grid item xs={6} sm={4} md={2}>
                    <KpiCard label="Not Found" value={summary.membersNotFound} color="#9e9e9e" />
                  </Grid>
                )}
                <Grid item xs={6} sm={4} md={2}>
                  <KpiCard label="Total Offer States" value={summary.totalOfferStates} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <KpiCard label="Outstanding (Liability)" value={summary.outstandingCount} color={summary.outstandingCount > 0 ? '#e65100' : '#2e7d32'} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <KpiCard label="Outstanding Value" value={fmtCurrency(summary.outstandingValue)} color={summary.outstandingValue > 0 ? '#e65100' : '#2e7d32'} />
                </Grid>
              </Grid>

              {results && results.length > 0 && (
                <ResultsTable results={results} onExportCSV={handleExportCSV} />
              )}
            </>
          )}
        </Box>
      </Container>
    </div>
  );
}
